import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import WebUiPageWrapper from '../../components/page-wrapper';

type LldpNeighborRow = {
  localPort: string;
  systemName: string;
  systemDescription: string;
  chassisType: string;
  chassisValue: string;
  portIdType: string;
  portIdValue: string;
  portDescription: string;
  age: string;
};

function childValue(obj: unknown, name: string): string {
  if (!obj || typeof obj !== 'object') return '';
  const rec = obj as Record<string, unknown>;
  if (typeof rec[name] === 'string' || typeof rec[name] === 'number')
    return String(rec[name]);
  Object.keys(rec).forEach((key) => {
    const found = childValue(rec[key], name);
    if (found) return found;
    return '';
  });
  return '';
}

export default function WebUiStatusLldpPage() {
  const [rows, setRows] = React.useState<LldpNeighborRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const data = await postWebUiApi<any>({
      service: 'lldp',
      access: 'get',
      command: 'get neighbors',
    });
    const source = data?.lldp?.interface;
    const list = (() => {
      if (Array.isArray(source)) return source;
      if (source) return [source];
      return [];
    })();

    setRows(
      list.map((item: any) => ({
        localPort: String(item?.interface ?? ''),
        systemName: String(
          Object.keys(item?.data?.chassis ?? {})[0] ?? '',
        ).replace(/^id$/, ''),
        systemDescription: childValue(item?.data?.chassis, 'descr'),
        chassisType: childValue(item?.data?.chassis, 'type'),
        chassisValue: childValue(item?.data?.chassis, 'value'),
        portIdType: String(item?.data?.port?.id?.type ?? ''),
        portIdValue: String(item?.data?.port?.id?.value ?? ''),
        portDescription: childValue(item?.data?.port, 'descr'),
        age: String(item?.data?.age ?? ''),
      })),
    );
  }, []);

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        await reload();
        if (alive) setError(null);
      } catch (err) {
        if (alive) setError((err as Error).message);
      }
    };
    load().catch(() => {});
    const timer = window.setInterval(() => load().catch(() => {}), 10000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [reload]);

  return (
    <WebUiPageWrapper titleList={['Status', 'LLDP']}>
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex flex-col gap-2">
        <h4 className="font-bold">LLDP Neighbours</h4>
        <table className="webui-table w-full">
          <thead>
            <tr className="webui-table-row-head">
              <th>Local Port</th>
              <th>System Name</th>
              <th>System Description</th>
              <th>Chassis ID Type</th>
              <th>Chassis ID Value</th>
              <td>Port ID Type</td>
              <th>Port ID Value</th>
              <th>Port Description</th>
              <th>Age</th>
            </tr>
            {rows.map((r, i) => (
              <tr
                key={`${r.localPort}-${i}`}
                className="webui-table-row-content"
              >
                <td>{r.localPort}</td>
                <td>{r.systemName}</td>
                <td>{r.systemDescription}</td>
                <td>{r.chassisType}</td>
                <td>{r.chassisValue}</td>
                <td>{r.portIdType}</td>
                <td>{r.portIdValue}</td>
                <td>{r.portDescription}</td>
                <td>{r.age}</td>
              </tr>
            ))}
          </thead>
        </table>
      </div>
    </WebUiPageWrapper>
  );
}
